import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../ui/Button';
import CapacityBadge from '../CapacityBadge';
import {
  buildMembershipRows,
  buildSeasonTiers,
  perSessionPrice,
  planCheapest,
  formatPrice,
  type IPricingOption,
  type IPricingSegment,
  type IPricingSelection,
  type IPricingTier,
} from '../../utils/coursePricing';
import { seasonTermLabel } from '../../utils/seasonLabel';
import type { ICourse } from '../../utils/courseSchedule';
import type { CourseMemberState } from './CourseRow';

/** Stable identity for a segment, for selection state. */
const segmentKey = (membershipId: number, seg: IPricingSegment): string =>
  `${membershipId}:${seg.scheme}:${seg.index ?? 'full'}`;

/**
 * Localized name for a division lane. Uses a dedicated plural key per scheme —
 * interpolating segmentUnit()'s singular noun produced "By half"/"By third".
 */
const tierLabel = (
  t: ReturnType<typeof useTranslation>['t'],
  tier: IPricingTier
): string => {
  switch (tier.scheme) {
    case 'full':
      return t('courses.tierWholeSeason');
    case 'halves':
      return t('courses.tierHalves');
    case 'thirds':
      return t('courses.tierThirds');
    case 'quarters':
      return t('courses.tierQuarters');
    default:
      return t('courses.tierParts', { count: tier.segments.length });
  }
};

/** One purchasable offer on a course: the drop-in price, or a plan. */
export interface ICourseOffer {
  key: string;
  name: string;
  /** Cheapest amount on this offer, for the tab's "from X" label. */
  cheapest: number | null;
  kind: 'dropin' | 'flat' | 'season';
  rows?: IPricingOption[];
  tiers?: IPricingTier[];
  membership?: NonNullable<ICourse['memberships']>[number];
}

/**
 * Every offer on a course, in display order: the drop-in price, then non-season
 * plans, then season plans.
 *
 * Exported as a hook so a host can ask "is anything priced here?" (and read the
 * cheapest price for a collapsed header) without duplicating the derivation.
 */
export const useCourseOffers = (
  course: ICourse,
  t: ReturnType<typeof useTranslation>['t'],
  locale: string
): { offers: ICourseOffer[]; dropIn: string | null } => {
  // Season plans expand into full/half/quarter tiers; everything else (calendar,
  // time, entry) keeps the flat label+price rows.
  const seasonPlans = useMemo(
    () =>
      (course.memberships || [])
        .filter((m) => m.type === 'season')
        .map((m) => ({
          membership: m,
          tiers: buildSeasonTiers(m, course.termStart, course.termEnd, locale),
        }))
        .filter((p) => p.tiers.length > 0),
    [course.memberships, course.termStart, course.termEnd, locale]
  );

  // Non-season plans, each kept as its own named offer rather than flattened
  // into a shared list — that flattening is what made "which price is which"
  // unreadable when a course offers several plans.
  const flatPlans = useMemo(
    () =>
      (course.memberships || [])
        .filter((m) => m.type !== 'season')
        .map((m) => ({ membership: m, rows: buildMembershipRows(m, t, locale) }))
        .filter((p) => p.rows.length > 0),
    [course.memberships, t, locale]
  );

  const dropIn = perSessionPrice(course, locale);

  const offers = useMemo(() => {
    const list: ICourseOffer[] = [];
    if (dropIn) {
      list.push({
        key: 'drop-in',
        name: t('courses.perSession'),
        cheapest: null, // its single price is already the tab's price
        kind: 'dropin',
      });
    }
    for (const { membership, rows } of flatPlans) {
      list.push({
        key: `plan-${membership._id}`,
        name: membership.name,
        cheapest: planCheapest(membership),
        kind: 'flat',
        rows,
        membership,
      });
    }
    for (const { membership, tiers } of seasonPlans) {
      list.push({
        key: `season-${membership._id}`,
        name: membership.name,
        cheapest: planCheapest(membership),
        kind: 'season',
        tiers,
        membership,
      });
    }
    return list;
  }, [dropIn, flatPlans, seasonPlans, t]);

  return { offers, dropIn };
};

interface CoursePricingPanelProps {
  course: ICourse;
  /** The currently selected member, or null when none chosen / logged out. */
  selectedMemberId?: number | null;
  /** This member's enrollment state for this course. */
  state?: CourseMemberState;
  /** Whether the course's active season is full (buy actions are disabled). */
  isFull?: boolean;
  /** True while a join/choose action is in flight for this course. */
  submitting?: boolean;
  /**
   * Bridge into the sign-up / choose-payment flow. Omit to keep the panel
   * read-only (public / logged-out browsing).
   */
  onChoose?: (course: ICourse, selection?: IPricingSelection) => void;
  /**
   * Show the season term + capacity strip. Turn off where the host already
   * displays both (e.g. the course detail header).
   */
  showHeader?: boolean;
  /** Fired after the CTA is pressed, so a modal host can close itself. */
  onAfterChoose?: () => void;
}

/**
 * The pricing content for one course: a tab per offer (drop-in, each plan), and
 * for a `season` plan its buyable stretches — whole season, halves, quarters —
 * each sized to the share of the term it covers, with a per-month rate and the
 * extra cost of splitting versus the cheapest route.
 *
 * Host-agnostic on purpose. `CoursePricePopover` renders it inside a modal from
 * the course card's price badge; `CourseDetail` renders it inline inside a
 * collapsible section. Same markup either way, so the two surfaces cannot drift.
 *
 * Renders nothing when the course has no priced offer.
 */
const CoursePricingPanel: React.FC<CoursePricingPanelProps> = ({
  course,
  selectedMemberId = null,
  state = 'none',
  isFull = false,
  submitting = false,
  onChoose,
  showHeader = true,
  onAfterChoose,
}) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  // The picked stretch, or null. Key drives the pressed state; selection is what
  // travels into the join flow.
  const [chosen, setChosen] = useState<{ key: string; selection: IPricingSelection } | null>(null);
  const [activeKey, setActiveKey] = useState<string | null>(null);

  const { offers, dropIn } = useCourseOffers(course, t, locale);

  if (offers.length === 0) return null;

  const active = offers.find((offer) => offer.key === activeKey) ?? offers[0];
  // A single offer needs no tab strip — it would be pure chrome.
  const showTabs = offers.length > 1;

  const focusTab = (delta: number) => {
    const i = offers.findIndex((offer) => offer.key === active.key);
    const next = offers[(i + delta + offers.length) % offers.length];
    setActiveKey(next.key);
  };

  const hasTerm = !!(course.termStart || course.termEnd);
  const season = hasTerm ? seasonTermLabel(course.termStart, course.termEnd, t, locale) : null;

  // A real action only makes sense for a selected member who has not yet joined.
  const canChoose = !!onChoose && selectedMemberId != null && state === 'none';

  return (
    <>
      {showHeader && (
        <div className="course-price-popover__head">
          {season && <span className="course-price-popover__season">{season}</span>}
          <CapacityBadge enrolled={course.enrolledCount ?? 0} capacity={course.capacity} />
        </div>
      )}

      {showTabs && (
        <div
          className="course-price-popover__tabs"
          role="tablist"
          aria-label={t('courses.pricing')}
        >
          {offers.map((offer) => {
            const isActive = offer.key === active.key;
            const from = offer.cheapest != null ? formatPrice(offer.cheapest, locale) : dropIn;
            return (
              <button
                key={offer.key}
                type="button"
                role="tab"
                id={`price-tab-${offer.key}`}
                aria-selected={isActive}
                aria-controls={`price-panel-${offer.key}`}
                className="course-price-popover__tab"
                onClick={() => setActiveKey(offer.key)}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowRight') {
                    e.preventDefault();
                    focusTab(1);
                  } else if (e.key === 'ArrowLeft') {
                    e.preventDefault();
                    focusTab(-1);
                  }
                }}
              >
                <span className="course-price-popover__tab-name">{offer.name}</span>
                {from && (
                  <span className="course-price-popover__tab-from">
                    {offer.kind === 'dropin' ? from : t('courses.priceFrom', { price: from })}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      <section
        className="course-price-popover__plan"
        role={showTabs ? 'tabpanel' : undefined}
        id={`price-panel-${active.key}`}
        aria-labelledby={showTabs ? `price-tab-${active.key}` : undefined}
      >
        {/* With tabs the name is the tab label; without them it needs a heading. */}
        {!showTabs && (
          <h4 className="course-price-popover__plan-name">{active.name}</h4>
        )}

        {active.kind === 'dropin' && (
          <ul className="course-price-popover__list">
            <li className="course-price-popover__item">
              <span className="course-price-popover__option-label">
                {t('courses.perSessionHint')}
              </span>
              <span className="course-price-popover__option-price">{dropIn}</span>
            </li>
          </ul>
        )}

        {active.kind === 'flat' && (
          <ul className="course-price-popover__list">
            {(active.rows || []).map((row, i) => (
              <li key={i} className="course-price-popover__item">
                <span className="course-price-popover__option-label">{row.label}</span>
                <span className="course-price-popover__option-price">{row.price}</span>
              </li>
            ))}
          </ul>
        )}

        {active.kind === 'season' && active.membership && (
          <>
            <p className="course-price-popover__plan-hint">{t('courses.planSeasonHint')}</p>

            {(active.tiers || []).map((tier) => {
              const total = formatPrice(tier.total, locale);
              const extra = formatPrice(tier.extraVsBest, locale);
              // Single-segment lanes (the whole season) already show their price
              // on the segment itself — a "total" repeat would be noise.
              const showTotal = tier.segments.length > 1;
              return (
                <div key={tier.scheme} className="course-price-popover__tier">
                  <div className="course-price-popover__tier-head">
                    <span className="course-price-popover__tier-name">
                      {tierLabel(t, tier)}
                    </span>
                    {tier.isBest ? (
                      <span className="course-price-popover__tier-sum course-price-popover__tier-sum--best">
                        {t('courses.tierBestValue')}
                      </span>
                    ) : (
                      <span className="course-price-popover__tier-sum">
                        {showTotal && total
                          ? `${t('courses.tierTotal', { total })} · `
                          : null}
                        <b>{t('courses.tierPremium', { amount: extra })}</b>
                      </span>
                    )}
                  </div>

                  <ul className="course-price-popover__segments">
                    {tier.segments.map((seg) => {
                      const key = segmentKey(active.membership!._id, seg);
                      const rate = seg.monthlyRate != null
                        ? formatPrice(Math.round(seg.monthlyRate), locale)
                        : null;
                      const body = (
                        <>
                          {seg.period && (
                            <span className="course-price-popover__seg-when">{seg.period}</span>
                          )}
                          <span className="course-price-popover__seg-price">
                            {formatPrice(seg.amount, locale)}
                          </span>
                          {rate && (
                            <span className="course-price-popover__seg-rate">
                              {t('courses.perMonthRate', { price: rate })}
                            </span>
                          )}
                        </>
                      );
                      return (
                        <li
                          key={key}
                          className="course-price-popover__segment"
                          style={{ flexGrow: Math.max(seg.fraction, 0.08) }}
                        >
                          {canChoose && !isFull ? (
                            <button
                              type="button"
                              className="course-price-popover__seg-btn"
                              aria-pressed={chosen?.key === key}
                              onClick={() =>
                                setChosen(
                                  chosen?.key === key
                                    ? null
                                    : {
                                        key,
                                        selection: {
                                          membershipId: active.membership!._id,
                                          segmentScheme: seg.scheme,
                                          segmentIndex: seg.index,
                                        },
                                      }
                                )
                              }
                            >
                              {body}
                            </button>
                          ) : (
                            <span className="course-price-popover__seg-static">{body}</span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}
          </>
        )}
      </section>

      <div className="course-price-popover__footer">
        {canChoose ? (
          isFull ? (
            <p className="course-price-popover__full">{t('courses.full')}</p>
          ) : (
            <Button
              fullWidth
              loading={submitting}
              // Only the season tab requires an explicit pick; on a drop-in or
              // flat-plan tab there is nothing to select, so don't block.
              disabled={submitting || (active.kind === 'season' && chosen == null)}
              onClick={() => {
                onAfterChoose?.();
                onChoose?.(course, chosen?.selection);
              }}
            >
              {t('courses.choosePlan')}
            </Button>
          )
        ) : (
          <p className="course-price-popover__readonly">
            {selectedMemberId == null
              ? t('courses.pricingSignInHint')
              : t('courses.pricingAlreadyEnrolled')}
          </p>
        )}
      </div>
    </>
  );
};

export default CoursePricingPanel;
